import { useRef, useState } from 'react';
import { ApiRoutes, AppRoutes } from 'src/lib/routes';
import { Formik, Form, Field } from 'formik';
import * as yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import { RECAPTCHA_KEY } from 'src/lib/constants';
import { omit } from 'lodash-es';
import { useRouter } from 'next/router';
import useCsrfTokenUpdate from 'src/hooks/useCsrfTokenUpdate';
import useCurrentUser from 'src/hooks/useCurrentUser';
import useRedirectIfLoggedIn from 'src/hooks/useRedirectIfLoggedIn';
import Loading from 'src/components/Loading';
import ApiClient from 'src/lib/api_client';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import InputPassword from 'src/components/InputPassword';
import classNames from 'classnames';
import useQuery from 'src/hooks/useQuery';
import { saveLastLoginMethod } from 'src/lib/last_login_method';
import useCart from 'src/hooks/useCart';
import { notifyLoggedInEvent } from 'src/helpers';
import { useTranslations } from 'next-intl';
import ErrorMesseageTranslation from 'src/components/ErrorMesseageTranslation';

const SignupValidationSchema = yup.object().shape({
  name: yup.string().required('validationNameRequired').typeError('validationNameTypeError'),
  email: yup
    .string()
    .email('validationEmailFormatError')
    .required('validationEmailRequired')
    .typeError('validationEmailTypeError'),
  password: yup.string().required('validationPassewordRequired').typeError('validationPassewordTypeError'),
  passwordConfirmation: yup
    .mixed()
    .test('match', 'validationConfirmPasswordRequired', (value, context) => {
      return context.parent.password === value;
    }),
  [RECAPTCHA_KEY]: yup
    .string()
    .required('validationCaptchaRequired')
    .typeError('validationCaptchaTypeError'),
});

type SignupFormProps = {
  name: string;
  email: string;
  password: string;
  passwordConfirmation: string;
  receiveNewletters: boolean;
  code?: string;
  [RECAPTCHA_KEY]: string;
};

const SignupForm: React.FC<{ inlineRender?: boolean, inputEmail?: string }> = ({ inlineRender, inputEmail }) => {
  const translation = useTranslations('components.login.SignupForm')
  const signupAccount = useEnhanceApiClient(ApiClient.signupAccount);
  const [error, setError] = useState<string>();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const updateCsrfToken = useCsrfTokenUpdate();
  const loginRef = useRedirectIfLoggedIn();
  const router = useRouter();
  const [_, setCurrentUser, saveToken] = useCurrentUser();
  const newEmail = inputEmail || router.query.email
  const { code, osc: orderShareCode } = router.query
  const [cart] = useCart();

  const { data, isLoading: isLoadingInvatition } = useQuery<{ id: number; email: string }>(
    code ? ApiRoutes.apiSupplierInvitationRoute({ id: String(code) }).toUrl() : null
  );

  const { email: signUpEmail, id: invitationId } = data || {
    email: newEmail ? String(newEmail) : '',
  };


  return isLoadingInvatition ? (
    <Loading className="tw-justify-center tw-py-20" />
  ) : (
    <Formik<SignupFormProps>
      validationSchema={SignupValidationSchema}
      initialValues={{
        name: '',
        email: signUpEmail || '',
        password: '',
        passwordConfirmation: '',
        receiveNewletters: true,
        code: code ? String(code) : '',
        [RECAPTCHA_KEY]: '',
      }}
      onSubmit={(values, { setSubmitting }) => {
        updateCsrfToken()
          .then((token) => {
            signupAccount(
              {
                user: omit(values, [RECAPTCHA_KEY, 'code']),
                [RECAPTCHA_KEY]: values[RECAPTCHA_KEY],
                code: values.code,
                osc: orderShareCode ? String(orderShareCode) : '',
                cartId: cart.id
              },
              { 'X-CSRF-Token': token }
            )
              .then((result) => {
                const [json] = result;
                if ('error' in json) {
                  setError(json.error);
                } else {
                  loginRef.current = true;
                  saveToken(json.token);
                  setCurrentUser(json.user);
                  notifyLoggedInEvent(json.user)
                  saveLastLoginMethod(json.user);
                  if (!inlineRender) {
                    window.location.href = AppRoutes.accountPhoneNumberRoute(
                      {},
                      { add_phone: true, ...router.query }
                    ).toUrl();
                  }
                }
              })
              .catch((e) => {
                setError(translation('anInternalErrorOccurred'));
                throw e;
              })
              .finally(() => setSubmitting(false));
          })
          .catch((e) => {
            setError(e.message);
            throw e;
          });
      }}
    >
      {(formik) => {
        return (
          <Form>
            <div className="form-group">
              <div className="input-group">
                <Field
                  name="name"
                  placeholder={translation('name')}
                  className="form-control"
                  autocomplete="name"
                />
              </div>
              <ErrorMesseageTranslation name="name" />
            </div>
            <div className="form-group">
              <div className="input-group">
                <Field
                  disabled={invitationId ? true : false}
                  name="email"
                  placeholder={translation('email')}
                  className="form-control"
                  autocomplete="email"
                />
              </div>
              <ErrorMesseageTranslation name="email" />
            </div>

            <div className="form-group">
              <InputPassword name="password" placeholder={translation('password')} />

              <ErrorMesseageTranslation name="password" />

              <div className="tw-text-sm tw-text-grey tw-mt-2">
                {translation('minimum6Characters')}
              </div>
            </div>

            <div className="form-group">
              <InputPassword name="passwordConfirmation" placeholder={translation('confirmPassword')} />
              <ErrorMesseageTranslation name="passwordConfirmation" />
            </div>

            <div className="form-group">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="checkbox-receive-newletters"
                  checked={formik.values.receiveNewletters}
                  onChange={(e) => {
                    formik.setFieldValue('receiveNewletters', e.target.checked);
                  }}
                />
                <label
                  className="form-check-label tw-text-sm form-check-sm"
                  htmlFor="checkbox-receive-newletters"
                >
                  {translation('IdLikeEmails')}
                </label>
              </div>
            </div>

            <div className="mb-3">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string}
                onChange={() => {
                  if (recaptchaRef.current) {
                    const recaptchaValue = recaptchaRef.current.getValue();
                    formik.setFieldValue(RECAPTCHA_KEY, recaptchaValue);
                  }
                }}
              />
              <ErrorMesseageTranslation name={RECAPTCHA_KEY} />
              <div
                className={classNames('invalid-feedback tw-block', { 'tw-hidden': !error })}
              >
                {error}
              </div>
            </div>

            <div className="form-group tw-text-center">
              <button type="submit" className="btn btn-primary">
                {translation('continue')}
              </button>
            </div>
          </Form>
        );
      }}
    </Formik>
  )
}

export default SignupForm
